import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { io } from 'socket.io-client';
import messages from '../statics/messages.json';

/**
 * PresenceProvider
 * ------------------------------------------------------------------
 * Owns the ONE socket the whole app uses for live presence. A single
 * connection matters because both the per-page roster and any global
 * "who's online" indicator read from it, and React 18 StrictMode would
 * otherwise churn a connection per mounting component.
 *
 * It carries no Cetec data - only { user, page, cell, value-on-save }.
 */

const PresenceContext = createContext(null);

const EMPTY_ROSTER = { here: [], cells: {}, locks: {} };

/** Same origin rule CommonContex uses: :8085 locally, same origin in production. */
const presenceOrigin = () => {
    const isLocal = window.location.hostname === 'localhost';
    const port = isLocal ? messages.node_server_port : '';
    return `${window.location.protocol}//${window.location.hostname}${port ? `:${port}` : ''}`;
};

const currentUser = () => {
    const email = localStorage.getItem('userEmail') || localStorage.getItem('email') || '';
    const name = localStorage.getItem('userName') || email;
    return { email: email.trim().toLowerCase(), name: (name || 'User').trim() };
};

export const PresenceProvider = ({ children }) => {
    const socketRef = useRef(null);
    const pageRef = useRef(null);
    /** Cells we are currently focused on -> whether we asked for a lock. Kept so a
     *  reconnect can restore both row-level focus and any editor lock we held. */
    const heldCellsRef = useRef(new Map());
    const saveHandlersRef = useRef(new Set());
    const lockDeniedHandlersRef = useRef(new Set());

    const [connected, setConnected] = useState(false);
    const [page, setPage] = useState(null);
    const [roster, setRoster] = useState(EMPTY_ROSTER);

    useEffect(() => {
        const socket = io(`${presenceOrigin()}/presence`, {
            withCredentials: true,
            // Polling first: presence still works if the host does not proxy
            // websockets, and socket.io upgrades on its own when it can.
            transports: ['polling', 'websocket'],
            autoConnect: true,
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            setConnected(true);
            if (!pageRef.current) return;
            socket.emit('presence:join', { page: pageRef.current, user: currentUser() });
            // Re-take anything we had open before the drop.
            heldCellsRef.current.forEach((lock, cell) =>
                socket.emit('presence:focus', { cell, lock })
            );
        });

        socket.on('disconnect', () => setConnected(false));

        socket.on('presence:roster', (data) => {
            if (!data || data.page !== pageRef.current) return;
            setRoster({
                here: data.here || [],
                cells: data.cells || {},
                locks: data.locks || {},
            });
        });

        socket.on('presence:lock-denied', (evt) => {
            if (evt?.cell) heldCellsRef.current.delete(evt.cell);
            lockDeniedHandlersRef.current.forEach((fn) => fn(evt));
        });

        socket.on('presence:saved', (evt) => {
            saveHandlersRef.current.forEach((fn) => fn(evt));
        });

        return () => {
            socket.removeAllListeners();
            socket.disconnect();
            socketRef.current = null;
        };
    }, []);

    /** Called by usePresence when a page mounts. Only one grid is mounted at a time. */
    const joinPage = useCallback((nextPage) => {
        if (!nextPage || pageRef.current === nextPage) return;
        pageRef.current = nextPage;
        heldCellsRef.current.clear();
        setPage(nextPage);
        setRoster(EMPTY_ROSTER);
        socketRef.current?.emit('presence:join', { page: nextPage, user: currentUser() });
    }, []);

    const leavePage = useCallback((leavingPage) => {
        if (pageRef.current !== leavingPage) return;
        // One blur with no cell clears everything we held on this page.
        socketRef.current?.emit('presence:blur', {});
        heldCellsRef.current.clear();
        pageRef.current = null;
        setPage(null);
        setRoster(EMPTY_ROSTER);
    }, []);

    const reportFocus = useCallback((cell, { lock = true } = {}) => {
        if (!cell) return;
        heldCellsRef.current.set(cell, lock);
        socketRef.current?.emit('presence:focus', { cell, lock });
    }, []);

    const reportBlur = useCallback((cell) => {
        if (!cell) return;
        heldCellsRef.current.delete(cell);
        socketRef.current?.emit('presence:blur', { cell });
    }, []);

    const broadcastSave = useCallback((payload) => {
        socketRef.current?.emit('presence:save', payload || {});
    }, []);

    const subscribeSave = useCallback((fn) => {
        saveHandlersRef.current.add(fn);
        return () => saveHandlersRef.current.delete(fn);
    }, []);

    const subscribeLockDenied = useCallback((fn) => {
        lockDeniedHandlersRef.current.add(fn);
        return () => lockDeniedHandlersRef.current.delete(fn);
    }, []);

    const value = useMemo(
        () => ({
            connected,
            page,
            roster,
            joinPage,
            leavePage,
            reportFocus,
            reportBlur,
            broadcastSave,
            subscribeSave,
            subscribeLockDenied,
        }),
        [
            connected,
            page,
            roster,
            joinPage,
            leavePage,
            reportFocus,
            reportBlur,
            broadcastSave,
            subscribeSave,
            subscribeLockDenied,
        ]
    );

    return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
};

export const usePresenceContext = () => useContext(PresenceContext);

export default PresenceProvider;
